import { Typography, Alert } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ImportPage } from 'services/imports/fileToPages';
import type { PageIssue } from 'views/imports/types';

type IssueView = { severity: 'error' | 'warning'; labelKey: string };

const resolveIssueView = (kind: PageIssue['kind']): IssueView => {
  switch (kind) {
    case 'ocr-error':
      return { severity: 'error', labelKey: 'imports:issues.ocrError' };
    case 'extract-error':
      return { severity: 'error', labelKey: 'imports:issues.extractError' };
    case 'review-error':
      return { severity: 'error', labelKey: 'imports:issues.reviewError' };
    case 'table-check':
      return { severity: 'warning', labelKey: 'imports:issues.tableCheck' };
    case 'no-lines':
      return { severity: 'warning', labelKey: 'imports:issues.noLines' };
    default: {
      const unreachable: never = kind;
      return unreachable;
    }
  }
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  heading: {
    fontWeight: 500,
  },
  detail: {
    wordBreak: 'break-word',
  },
  hint: {
    color: theme.palette.text.secondary,
  },
}));

type IssuesListProps = {
  issues: readonly PageIssue[];
  pages: readonly ImportPage[];
  cancelled: boolean;
};

export const IssuesList = ({ issues, pages, cancelled }: IssuesListProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (issues.length === 0 && !cancelled) return null;

  const pageById = new Map(pages.map((page) => [page.id, page]));
  const hasErrors = issues.some((issue) => resolveIssueView(issue.kind).severity === 'error');
  const hasRetriableErrors = issues.some((issue) => issue.kind === 'ocr-error' || issue.kind === 'extract-error');

  const headingFor = (issue: PageIssue, label: string): string => {
    const page = pageById.get(issue.pageId);
    if (!page) return label;
    return `${page.fileName} - ${t('imports:viewer.page', { page: page.pageNumber })}: ${label}`;
  };

  return (
    <div className={classes.root}>
      {cancelled && (
        <Alert severity="info">{t('imports:extract.cancelled')}</Alert>
      )}
      {issues.map((issue) => {
        const view = resolveIssueView(issue.kind);
        return (
          <Alert key={`${issue.pageId}-${issue.kind}-${issue.detail ?? ''}`} severity={view.severity}>
            <Typography variant="body2" className={classes.heading}>
              {headingFor(issue, t(view.labelKey))}
            </Typography>
            {issue.detail && (
              <Typography variant="caption" display="block" className={classes.detail}>
                {issue.detail}
              </Typography>
            )}
          </Alert>
        );
      })}
      {hasRetriableErrors && hasErrors && (
        <Typography variant="caption" className={classes.hint}>
          {t('imports:extract.retryHint')}
        </Typography>
      )}
    </div>
  );
};
